export interface TimezoneOption {
  id: string;
  label: string;
}

// Curated list — common business timezones, not the full IANA database.
const CURATED_ZONE_IDS = [
  "Pacific/Honolulu",
  "America/Anchorage",
  "America/Los_Angeles",
  "America/Denver",
  "America/Chicago",
  "America/New_York",
  "America/Sao_Paulo",
  "America/Argentina/Buenos_Aires",
  "Atlantic/Azores",
  "Europe/London",
  "Europe/Lisbon",
  "Europe/Madrid",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Athens",
  "Africa/Johannesburg",
  "Europe/Moscow",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Bangkok",
  "Asia/Singapore",
  "Asia/Shanghai",
  "Asia/Tokyo",
  "Australia/Sydney",
  "Pacific/Auckland",
];

function offsetLabel(zoneId: string): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: zoneId,
    timeZoneName: "shortOffset",
  }).formatToParts(new Date());
  const tzPart = parts.find((p) => p.type === "timeZoneName");
  return tzPart?.value.replace("GMT", "UTC") ?? "";
}

function cityLabel(zoneId: string): string {
  const last = zoneId.split("/").pop() ?? zoneId;
  return last.replace(/_/g, " ");
}

export function listTimezones(): TimezoneOption[] {
  return CURATED_ZONE_IDS.map((id) => ({
    id,
    label: `${cityLabel(id)} (${offsetLabel(id)})`,
  })).sort((a, b) => offsetSortKey(a.id) - offsetSortKey(b.id));
}

function offsetSortKey(zoneId: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: zoneId,
    timeZoneName: "shortOffset",
  }).formatToParts(new Date());
  const raw = parts.find((p) => p.type === "timeZoneName")?.value ?? "GMT+0";
  const match = raw.match(/GMT([+-]\d+)(?::(\d+))?/);
  if (!match) return 0;
  const hours = Number(match[1]);
  const minutes = Number(match[2] ?? 0);
  return hours * 60 + Math.sign(hours || 1) * minutes;
}

export function detectTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Sao_Paulo";
  } catch {
    return "America/Sao_Paulo";
  }
}

export function loadTimezone(): string {
  if (typeof localStorage !== "undefined") {
    const saved = localStorage.getItem("tz");
    if (saved) return saved;
  }
  return detectTimezone();
}

export function saveTimezone(tz: string) {
  if (typeof localStorage !== "undefined") localStorage.setItem("tz", tz);
}
